import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { apiGet } from '../../api/client'
import { n, s } from './AutonomyPage'

type MetricsRow = Record<string, unknown>

type MetricsPage = {
  ok: boolean
  rows?: MetricsRow[]
  headline?: Record<string, unknown>
  today?: Record<string, unknown>
}

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

function deskMetricsQuery(dscope: string, from: string, to: string) {
  const p = new URLSearchParams({ from, to })
  if (dscope) p.set('dscope', dscope)
  return {
    queryKey: ['settings', 'autonomy', 'desk-metrics', dscope, from, to] as const,
    queryFn: () => apiGet<MetricsPage>(`/settings/autonomy/desk/metrics?${p.toString()}`),
  }
}

function pct(rate: unknown): string {
  const v = Number(rate ?? 0)
  if (!Number.isFinite(v) || v <= 0) return '0%'
  return `${v}%`
}

function avg(a: unknown, b: unknown): string {
  const num = Number(a ?? 0)
  const den = Number(b ?? 0)
  if (den <= 0) return '—'
  return String(Math.round((num / den) * 10) / 10)
}

export function DeskMetricsPanel({ dscope }: { dscope: string }) {
  const [days, setDays] = useState(7)
  const range = useMemo(() => {
    const to = isoDaysAgo(0)
    const from = isoDaysAgo(Math.max(1, days) - 1)
    return { from, to }
  }, [days])
  const q = useQuery({ ...deskMetricsQuery(dscope, range.from, range.to), enabled: true })
  const headline = q.data?.headline ?? {}
  const rows = q.data?.rows ?? []
  const byType = useMemo(() => {
    const map = new Map<string, MetricsRow>()
    for (const row of rows) {
      const key = s(row.request_type) || 'other'
      const prev = map.get(key) ?? {}
      const add = (k: string) => Number(prev[k] ?? 0) + Number(row[k] ?? 0)
      map.set(key, {
        request_type: key,
        taken: add('taken'),
        confirmed: add('confirmed'),
        held: add('held'),
        silent: add('silent'),
        reopened: add('reopened'),
        not_fixed: add('not_fixed'),
        handed: add('handed'),
        failed: add('failed'),
        rounds_sum: add('rounds_sum'),
        cost_usd: add('cost_usd'),
      })
    }
    return [...map.values()].sort((a, b) => s(a.request_type).localeCompare(s(b.request_type)))
  }, [rows])

  return (
    <div className="panel panel-default" id="odsk-metrics" style={{ marginTop: 12 }}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Desk metrics</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            autonomous resolution rate, cost, and breakdown by request type. Today is computed live; earlier days are filled nightly.
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, fontSize: 12 }}>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                className={`btn btn-xs ${days === d ? 'btn-primary' : 'btn-default'}`}
                onClick={() => setDays(d)}
              >
                {d} days
              </button>
            ))}
          </span>
        </div>
        {q.isLoading && <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>Loading…</div>}
        {q.data?.ok && (
          <>
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <strong>Autonomous resolution:</strong> {pct(headline.rate)}
              {' · '}
              <span className="text-muted">
                {n(headline.resolved)} of {n(headline.taken)} taken
                {n(headline.silent) > 0 ? ` (silent ${n(headline.silent)})` : ''}
                {' · '}
                cost / resolved {Number(headline.cost_per_resolved ?? 0).toFixed(2)} USD
                {headline.csat != null ? ` · CSAT ${Number(headline.csat).toFixed(1)}` : ''}
              </span>
            </div>
            <table className="table table-condensed" style={{ fontSize: 12, marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Request type</th>
                  <th style={{ textAlign: 'right' }}>Taken</th>
                  <th style={{ textAlign: 'right' }}>Confirmed</th>
                  <th style={{ textAlign: 'right' }}>Held</th>
                  <th style={{ textAlign: 'right' }}>Silent</th>
                  <th style={{ textAlign: 'right' }}>Reopened</th>
                  <th style={{ textAlign: 'right' }}>Not fixed</th>
                  <th style={{ textAlign: 'right' }}>Handed</th>
                  <th style={{ textAlign: 'right' }}>Failed</th>
                  <th style={{ textAlign: 'right' }}>Avg rounds</th>
                  <th style={{ textAlign: 'right' }}>Cost USD</th>
                </tr>
              </thead>
              <tbody>
                {byType.length === 0 ? (
                  <tr><td colSpan={11} className="text-muted">No rolled-up days in this interval yet.</td></tr>
                ) : (
                  byType.map((row) => (
                    <tr key={s(row.request_type)}>
                      <td><code>{s(row.request_type)}</code></td>
                      <td style={{ textAlign: 'right' }}>{n(row.taken)}</td>
                      <td style={{ textAlign: 'right' }}>{n(row.confirmed)}</td>
                      <td style={{ textAlign: 'right' }}>{n(row.held)}</td>
                      <td style={{ textAlign: 'right' }}>{n(row.silent)}</td>
                      <td style={{ textAlign: 'right' }}>{n(row.reopened)}</td>
                      <td style={{ textAlign: 'right' }}>{n(row.not_fixed)}</td>
                      <td style={{ textAlign: 'right' }} title="no_resolver, limits, incident, …">{n(row.handed)}</td>
                      <td style={{ textAlign: 'right' }} title="lab_error, timeout, budget, …">{n(row.failed)}</td>
                      <td style={{ textAlign: 'right' }}>{avg(row.rounds_sum, row.taken)}</td>
                      <td style={{ textAlign: 'right' }}>{Number(row.cost_usd ?? 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  )
}
